import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Transaction } from '../App';
import { toast } from 'sonner';
import { X, Search, Send, User as UserIcon, Copy, Shield } from 'lucide-react';

interface TransModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
}

interface WalletUser {
  id: number;
  nickname: string;
  walletAddress: string;
  referralCode: string;
  balance: number;
}

export function TransModal({ isOpen, onClose, user, onUpdateBalance, onAddTransaction }: TransModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  
  // 보내는 지갑 관련 상태
  const [senderSearchQuery, setSenderSearchQuery] = useState('');
  const [senderSearchResults, setSenderSearchResults] = useState([]);
  const [selectedSender, setSelectedSender] = useState(user);
  const [isSenderSearching, setIsSenderSearching] = useState(false);

  // 재단 지갑 정보 (고정)
  const foundationWallet: WalletUser = {
    id: 22,
    nickname: '재단',
    walletAddress: '0x0000000000000000000000000000000000A88888',
    referralCode: 'A88888',
    balance: 995901.00
  };

  // 보내는 지갑 검색 함수
  const searchSenderWallets = async (query: string) => {
    if (!query.trim()) {
      setSenderSearchResults([]);
      return;
    }

    setIsSenderSearching(true);
    try {
      const token = localStorage.getItem('vatton-jwt-token');
      const response = await fetch(`http://159.223.63.184:3601/api/vatton/admin/users?search=${encodeURIComponent(query)}&limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let results: WalletUser[] = [];

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data?.users) {
          results = data.data.users.map((user: any) => ({
            id: user.id,
            nickname: user.nickname,
            walletAddress: user.wallet_address,
            referralCode: user.referral_code,
            balance: user.vttn_balance || 0
          }));
        }
      }

      // 재단 지갑도 검색 결과에 포함
      const queryLower = query.toLowerCase();
      if (queryLower.includes('재단') || 
          queryLower.includes('a88888') || 
          queryLower.includes(foundationWallet.walletAddress.toLowerCase()) ||
          queryLower.includes('foundation')) {
        if (!results.find(r => r.id === foundationWallet.id)) {
          results.unshift(foundationWallet);
        }
      }

      setSenderSearchResults(results);
    } catch (error) {
      console.error('보내는 지갑 검색 오류:', error);
      toast.error('보내는 지갑 검색 중 오류가 발생했습니다.');
      setSenderSearchResults([]);
    } finally {
      setIsSenderSearching(false);
    }
  };

  // 받는 지갑 검색 함수
  const searchWallets = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('vatton-jwt-token');
      const response = await fetch(`http://159.223.63.184:3601/api/vatton/admin/users?search=${encodeURIComponent(query)}&limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let results: WalletUser[] = [];

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data?.users) {
          results = data.data.users.map((user: any) => ({
            id: user.id,
            nickname: user.nickname,
            walletAddress: user.wallet_address,
            referralCode: user.referral_code,
            balance: user.vttn_balance || 0
          }));
        }
      }

      // 재단 지갑도 검색 결과에 포함 (검색어가 재단, A88888, 또는 재단 지갑 주소와 일치할 때)
      const queryLower = query.toLowerCase();
      if (queryLower.includes('재단') || 
          queryLower.includes('a88888') || 
          queryLower.includes(foundationWallet.walletAddress.toLowerCase()) ||
          queryLower.includes('foundation')) {
        // 재단 지갑이 이미 결과에 없으면 추가
        if (!results.find(r => r.id === foundationWallet.id)) {
          results.unshift(foundationWallet);
        }
      }

      setSearchResults(results);
    } catch (error) {
      console.error('지갑 검색 오류:', error);
      toast.error('지갑 검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (searchQuery.length > 0) {
      const timeoutId = setTimeout(() => {
        searchWallets(searchQuery);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (senderSearchQuery.length > 0) {
      const timeoutId = setTimeout(() => {
        searchSenderWallets(senderSearchQuery);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setSenderSearchResults([]);
    }
  }, [senderSearchQuery]);

  const handleSendTokens = () => {
    if (!selectedSender) {
      toast.error('보내는 지갑을 선택해주세요.');
      return;
    }

    if (!selectedUser) {
      toast.error('받는 지갑을 선택해주세요.');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('올바른 금액을 입력해주세요.');
      return;
    }

    const sendAmount = parseFloat(amount);
    if (sendAmount > (selectedSender?.balance || 0)) {
      toast.error('잔고가 부족합니다.');
      return;
    }

    // 보내는 지갑과 받는 지갑, 금액이 모두 선택/입력되었을 때만 PIN 입력 모달 표시
    if (selectedSender && selectedUser && amount) {
      setPinInput(''); // 모달 열 때 PIN 초기화
      setShowPinModal(true);
    }
  };

  const handlePinSubmit = async () => {
    if (!pinInput) {
      toast.error('PIN을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setShowPinModal(false);

    try {
      // 실제 토큰 전송 API 호출
      const token = localStorage.getItem('vatton-jwt-token');
      const response = await fetch('http://159.223.63.184:3601/api/wallet/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from: selectedSender?.walletAddress,
          to: selectedUser.walletAddress,
          amount: parseFloat(amount).toString(),
          pin: pinInput
        })
      });

      const result = await response.json();

      if (result.success) {
        const sendAmount = parseFloat(amount);
        const newBalance = (selectedSender?.balance || 0) - sendAmount;
        onUpdateBalance(newBalance);
        
        const newTransaction: Transaction = {
          id: Date.now(),
          type: `Transfer to ${selectedUser.nickname}`,
          amount: -sendAmount,
          createdAt: new Date().toISOString()
        };
        onAddTransaction(newTransaction);
        
        toast.success(`${sendAmount} VTTN을 ${selectedUser.nickname}에게 전송했습니다.`);
        setAmount('');
        setSelectedUser(null);
        setSearchQuery('');
        setSenderSearchQuery('');
        setSenderSearchResults([]);
        setSearchResults([]);
        setSelectedSender(null);
        setPinInput('');
        onClose();
      } else {
        toast.error(result.error || '전송에 실패했습니다.');
      }
    } catch (error) {
      console.error('전송 오류:', error);
      toast.error('전송에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('주소가 복사되었습니다!');
    } catch (error) {
      toast.error('주소 복사에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl bg-card border-border max-h-[90vh] overflow-auto">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">VTTN 전송</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* 좌우 지갑 레이아웃 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 왼쪽: 보내는 지갑 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">보내는 지갑</h3>
              
              {/* 선택된 보내는 지갑 정보 */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                    <UserIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{selectedSender?.nickname}</p>
                    <p className="text-sm text-muted-foreground">코드: {selectedSender?.referralCode}</p>
                  </div>
                </div>
                <p className="text-blue-700 dark:text-blue-300 font-medium mb-1">현재 잔고</p>
                <p className="text-2xl font-bold text-foreground">{(selectedSender?.balance || 0).toFixed(2)} VTTN</p>
                <p className="text-xs text-muted-foreground mt-2 font-mono">
                  {selectedSender?.walletAddress?.slice(0, 10)}...{selectedSender?.walletAddress?.slice(-8)}
                </p>
              </div>

              {/* 보내는 지갑 검색 */}
              <div className="space-y-2">
                <Label htmlFor="sender-search">내 지갑리스트</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="sender-search"
                    placeholder="닉네임, 추천코드 또는 지갑주소 입력"
                    value={senderSearchQuery}
                    onChange={(e) => setSenderSearchQuery(e.target.value)}
                    className="pl-10 border-border bg-input-background"
                  />
                </div>

                {/* 보내는 지갑 검색 결과 */}
                {isSenderSearching && (
                  <div className="text-center py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto"></div>
                  </div>
                )}

                {senderSearchResults.length > 0 && (
                  <div className="space-y-1 max-h-32 overflow-auto">
                    {senderSearchResults.map((walletUser) => (
                      <div
                        key={walletUser.id}
                        onClick={() => setSelectedSender(walletUser)}
                        className={`p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                          selectedSender?.id === walletUser.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">{walletUser.nickname}</p>
                            <p className="text-xs text-muted-foreground">{walletUser.balance.toFixed(2)} VTTN</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 전송 금액 입력 */}
              <div className="space-y-2">
                <Label htmlFor="amount">보낼 자산 입력 (VTTN)</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="border-border bg-input-background"
                  disabled={!selectedUser}
                />
              </div>
            </div>

            {/* 오른쪽: 받는 지갑 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">받는 지갑</h3>
              
              {/* 선택된 받는 지갑 정보 */}
              {selectedUser ? (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{selectedUser.nickname}</p>
                      <p className="text-sm text-muted-foreground">코드: {selectedUser.referralCode}</p>
                    </div>
                  </div>
                  <p className="text-green-700 dark:text-green-300 font-medium mb-1">받을 자산 내역</p>
                  <p className="text-2xl font-bold text-foreground">
                    {(selectedUser.balance + (amount ? parseFloat(amount) : 0)).toFixed(2)} VTTN
                  </p>
                  {amount && parseFloat(amount) > 0 && (
                    <p className="text-sm text-green-600">
                      +{parseFloat(amount).toFixed(2)} VTTN (전송 예정)
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 font-mono">
                    {selectedUser.walletAddress.slice(0, 10)}...{selectedUser.walletAddress.slice(-8)}
                  </p>
                </div>
              ) : (
                <div className="bg-muted/50 rounded-xl p-8 text-center">
                  <p className="text-muted-foreground">받는 지갑을 선택해주세요</p>
                </div>
              )}

              {/* 받는 지갑 검색 */}
              <div className="space-y-2">
                <Label htmlFor="search">받는 지갑리스트</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="닉네임, 추천코드 또는 지갑주소 입력"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-border bg-input-background"
                  />
                </div>

                {/* 검색 결과 */}
                {isSearching && (
                  <div className="text-center py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto"></div>
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="space-y-1 max-h-32 overflow-auto">
                    {searchResults.map((walletUser) => (
                      <div
                        key={walletUser.id}
                        onClick={() => setSelectedUser(walletUser)}
                        className={`p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                          selectedUser?.id === walletUser.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">{walletUser.nickname}</p>
                            <p className="text-xs text-muted-foreground">{walletUser.balance.toFixed(2)} VTTN</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyAddress(walletUser.walletAddress);
                            }}
                            className="p-1 hover:bg-muted rounded-lg transition-colors"
                          >
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchQuery.length > 0 && searchResults.length === 0 && !isSearching && (
                  <div className="text-center py-2">
                    <p className="text-muted-foreground text-sm">검색 결과가 없습니다.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 전송 버튼 */}
          <div className="mt-6">
            <Button
              onClick={handleSendTokens}
              disabled={!selectedSender || !selectedUser || !amount || isLoading}
              className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>전송 중...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Send className="h-4 w-4" />
                  <span>VTTN 전송</span>
                </div>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* PIN 입력 모달 */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">보안 확인</h3>
              <p className="text-muted-foreground text-sm">
                {selectedSender?.nickname}에서 {selectedUser?.nickname}으로 {amount} VTTN 전송
              </p>
              <p className="text-muted-foreground text-sm mt-1">PIN을 입력하여 전송을 확인해주세요</p>
            </div>

            <div className="space-y-4">
              <div>
                <input
                  type="password"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  placeholder="PIN 번호 입력"
                  maxLength={10}
                  autoFocus
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handlePinSubmit();
                    }
                  }}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowPinModal(false);
                    setPinInput('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  취소
                </Button>
                <Button
                  onClick={handlePinSubmit}
                  disabled={!pinInput || isLoading}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                >
                  {isLoading ? '전송 중...' : '전송 확인'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}